import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import pickBy from "lodash/pickBy";

import { selectAllBanners, useGetBannersQuery } from "fond/api/bannerSlice";
import { MINUTE } from "fond/constants";
import { Banner } from "fond/types";
import localStorage from "fond/utils/localStorage";

const BANNER_POLLING_INTERVAL = 5 * MINUTE;

/**
 * LocalStorage key for storing dismissed banner state
 * Format: { [bannerId]: lastModifiedAt }
 */
export const DISMISSED_BANNERS_KEY = "FOND_DISMISSED_BANNERS";

type DismissedBannersStorage = Record<string, string>;

/**
 * Get dismissed banners from localStorage
 */
const getDismissedBanners = (): DismissedBannersStorage => {
  return localStorage.getItem(DISMISSED_BANNERS_KEY, {});
};

/**
 * Store a banner dismissal in localStorage
 */
const dismissBanner = (id: string, lastModifiedAt: string): DismissedBannersStorage => {
  const dismissed = getDismissedBanners();
  dismissed[id] = lastModifiedAt;
  localStorage.setItem(DISMISSED_BANNERS_KEY, dismissed);
  return dismissed;
};

/**
 * Prune any unknown dismissed banners.
 */
const pruneUnknownBanners = (activeBannerIds: string[]): DismissedBannersStorage => {
  const activeBannerIdSet = new Set(activeBannerIds);
  const cleaned = pickBy(getDismissedBanners(), (_, id) => activeBannerIdSet.has(id));
  localStorage.setItem(DISMISSED_BANNERS_KEY, cleaned);
  return cleaned;
};

/**
 * Return whether the given banner is dismissed.
 */
const isBannerDismissed = (banner: Banner, dismissed: DismissedBannersStorage): boolean => {
  if (banner.DisplayMode !== "dismissable") {
    return false;
  }

  const dismissedAt = dismissed[banner.ID];
  if (!dismissedAt) {
    return false;
  }

  // Show banner if it has been updated since dismissal
  return new Date(banner.LastModifiedAt) <= new Date(dismissedAt);
};

interface UseBannersReturns {
  // Array of visible (enabled, non-dismissed) banners
  banners: Banner[];
  // Whether the banners are loading for the first time
  isLoading: boolean;
  // Callback to dismiss a banner
  dismiss: (banner: Banner) => void;
}

export const useBanners = (): UseBannersReturns => {
  const { isLoading, isSuccess } = useGetBannersQuery(undefined, {
    pollingInterval: BANNER_POLLING_INTERVAL,
  });
  const banners = useSelector(selectAllBanners);
  const [dismissedBanners, setDismissedBanners] = useState<DismissedBannersStorage>(getDismissedBanners());

  const visibleBanners = useMemo(() => {
    return banners.filter((banner) => !isBannerDismissed(banner, dismissedBanners));
  }, [banners, dismissedBanners]);

  const dismiss = useCallback((banner: Banner) => {
    const dismissed = dismissBanner(banner.ID, banner.LastModifiedAt);
    setDismissedBanners(dismissed);
  }, []);

  // Remove dismissed banners from localStorage that are no longer present in the API response
  useEffect(() => {
    if (isSuccess) {
      const dismissed = pruneUnknownBanners(banners.map((banner) => banner.ID));
      setDismissedBanners(dismissed);
    }
  }, [isSuccess, banners]);

  return useMemo(
    () => ({
      banners: visibleBanners,
      isLoading,
      dismiss,
    }),
    [dismiss, isLoading, visibleBanners]
  );
};
